import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Tag, message } from "antd";
import { CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { taskActualApis } from "../../../api/TaskSchedule";

// Month-grid editor for a task's monthly ACTUAL execution — the executed twin
// of TaskScheduleFormModal. Rows show the scheduled (target) quantity
// read-only beside each month's actual input; "Copy scheduled" fills actuals
// from the targets. Save is a full REPLACE of the task's actuals: only months
// with an entered quantity are sent (empty grid = clear, confirmed first).
// Under-execution is normal (no warning); over-execution / out-of-window /
// future months come back as amber non-blocking warnings from the save.

const { RangePicker } = DatePicker;

const round4 = (n: number) => Math.round(n * 10000) / 10000;

export interface ActualTaskInfo {
  taskId: number;
  taskName?: string;
  workQuantity?: number | string | null;
  workUomName?: string;
}

interface ActualRow {
  month: string; // 'YYYY-MM'
  scheduled: number;
  qty: number | null;
  remarks: string;
  // Filled by daily execution and owned by it. The server rejects a hand-entered
  // value on such a month, so the input is locked rather than allowed to bounce.
  isDerived: boolean;
}

const monthsBetween = (start: Dayjs, end: Dayjs): string[] => {
  const list: string[] = [];
  let cursor = start.startOf("month");
  const last = end.startOf("month");
  while (cursor.isBefore(last) || cursor.isSame(last, "month")) {
    list.push(cursor.format("YYYY-MM"));
    cursor = cursor.add(1, "month");
  }
  return list;
};

const TaskActualFormModal = ({
  open,
  onClose,
  task,
  initialMonths,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  task: ActualTaskInfo;
  initialMonths: Array<{
    actualMonth: string;
    actualQty: string;
    scheduledQty: string;
    remarks?: string;
    source?: string; // 'DERIVED' | 'MANUAL' | '' (scheduled only)
  }>;
  onSaved?: () => void;
}) => {
  const [rows, setRows] = useState<ActualRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const workQuantity = Number(task?.workQuantity) || 0;
  const uom = task?.workUomName || "";
  const currentMonth = dayjs().format("YYYY-MM");

  useEffect(() => {
    if (!open) return;
    setError("");
    setSaving(false);
    // Grid seeded from schedule∪actual months (the GET already unions them).
    // The union marks scheduled-but-unactualized months with actualQty '0' —
    // shown as EMPTY here (a remark keeps an explicit saved 0 distinguishable).
    setRows(
      (initialMonths || []).map((m) => ({
        month: dayjs(m.actualMonth).format("YYYY-MM"),
        scheduled: Number(m.scheduledQty) || 0,
        qty: Number(m.actualQty) > 0 ? Number(m.actualQty) : m.remarks ? 0 : null,
        remarks: m.remarks || "",
        isDerived: m.source === "DERIVED",
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Any month owned by daily execution. Adding months around them is still
  // fine — only those months themselves are locked.
  const derivedCount = useMemo(
    () => rows.filter((row) => row.isDerived).length,
    [rows]
  );

  const range: [Dayjs, Dayjs] | null = useMemo(() => {
    if (!rows.length) return null;
    return [dayjs(`${rows[0].month}-01`), dayjs(`${rows[rows.length - 1].month}-01`)];
  }, [rows]);

  const onRangeChange = useCallback(
    (value: null | [Dayjs | null, Dayjs | null]) => {
      if (!value || !value[0] || !value[1]) {
        setRows([]);
        return;
      }
      const monthList = monthsBetween(value[0], value[1]);
      setRows((prev) => {
        const prevRow = new Map(prev.map((r) => [r.month, r]));
        return monthList.map((month) => {
          const existing = prevRow.get(month);
          // A month the user adds by widening the range is theirs to fill.
          return existing ?? { month, scheduled: 0, qty: null, remarks: "", isDerived: false };
        });
      });
    },
    []
  );

  const copyScheduled = useCallback(() => {
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        qty: row.scheduled > 0 ? row.scheduled : row.qty,
      }))
    );
  }, []);

  const entered = useMemo(() => rows.filter((row) => row.qty !== null), [rows]);
  const total = useMemo(
    () => round4(entered.reduce((acc, row) => acc + (Number(row.qty) || 0), 0)),
    [entered]
  );
  // Over-execution only (under is the normal in-progress state) — mirrors the
  // backend's asymmetric actualSumWarning with the same ±1 tolerance.
  const SUM_TOLERANCE = 1;
  const overBy = workQuantity > 0 ? Number((total - workQuantity).toFixed(2)) : 0;
  const overExecuted = workQuantity > 0 && overBy > SUM_TOLERANCE;
  const futureRows = useMemo(
    () => entered.filter((row) => row.month > currentMonth),
    [entered, currentMonth]
  );

  const doSave = useCallback(async () => {
    try {
      setSaving(true);
      setError("");
      const res = await taskActualApis.save(
        Number(task.taskId),
        entered.map((row) => ({
          actualMonth: `${row.month}-01`,
          actualQty: String(round4(Number(row.qty) || 0)),
          ...(row.remarks.trim() ? { remarks: row.remarks.trim() } : {}),
        }))
      );
      if (res?.status === true) {
        message.success(res?.data?.message || "Task actuals saved");
        if (res?.data?.warning) message.warning(res.data.warning, 6);
        onSaved?.();
        onClose();
      } else {
        setError(res?.message || "Failed to save the actuals");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to save the actuals");
    } finally {
      setSaving(false);
    }
  }, [task?.taskId, entered, onSaved, onClose]);

  const onSaveClick = useCallback(() => {
    if (!entered.length) {
      Modal.confirm({
        title: "Clear this task's actuals?",
        content:
          "No actual quantities are set — saving will remove the task's monthly actuals entirely.",
        okText: "Clear Actuals",
        okButtonProps: { danger: true },
        onOk: doSave,
      });
      return;
    }
    doSave();
  }, [entered.length, doSave]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={
        <span style={{ color: "#022c57" }}>
          Monthly Actual Execution
          {task?.taskName ? (
            <span style={{ color: "#8094ae", fontWeight: 400 }}>
              {" "}
              — {task.taskName}
            </span>
          ) : null}
        </span>
      }
      destroyOnClose
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <RangePicker
          picker="month"
          format="MMM YYYY"
          value={range}
          onChange={onRangeChange}
          allowClear
        />
        <Button
          icon={<CopyOutlined />}
          onClick={copyScheduled}
          disabled={!rows.some((row) => row.scheduled > 0)}
        >
          Copy scheduled
        </Button>
      </div>

      {workQuantity > 0 && (
        <div style={{ color: "#526484", fontSize: 12, marginBottom: 8 }}>
          Task quantity: <b style={{ color: "#022c57" }}>{workQuantity}</b>
          {uom ? ` ${uom}` : ""} — enter what actually got executed each month.
        </div>
      )}

      {!rows.length ? (
        <div
          style={{
            border: "1px dashed #dbdfea",
            borderRadius: 6,
            padding: "18px 12px",
            textAlign: "center",
            color: "#8094ae",
            marginBottom: 12,
          }}
        >
          Pick a start and end month to build the actuals grid.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #dbdfea",
            borderRadius: 6,
            marginBottom: 12,
            maxHeight: 320,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 12px",
              fontSize: 11,
              color: "#8094ae",
              borderBottom: "1px solid #f0f2f7",
              position: "sticky",
              top: 0,
              background: "#fff",
              zIndex: 1,
            }}
          >
            <span style={{ width: 130 }}>Month</span>
            <span style={{ width: 90, textAlign: "right" }}>Planned</span>
            <span style={{ flex: 1 }}>Actual Qty</span>
            <span style={{ flex: 1 }}>Remarks</span>
            <span style={{ width: 24 }} />
          </div>
          {rows.map((row) => (
            <div
              key={row.month}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 12px",
                borderTop: "1px solid #f0f2f7",
              }}
            >
              <span style={{ width: 130, color: "#022c57", fontWeight: 500 }}>
                {dayjs(`${row.month}-01`).format("MMM YYYY")}
                {row.month > currentMonth && (
                  <Tag color="gold" style={{ marginLeft: 6, fontSize: 10, lineHeight: "16px" }}>
                    future
                  </Tag>
                )}
                {row.isDerived && (
                  <Tag color="blue" style={{ marginInlineStart: 6 }}>
                    From execution
                  </Tag>
                )}
              </span>
              <span style={{ width: 90, textAlign: "right", color: "#8094ae" }}>
                {row.scheduled > 0
                  ? row.scheduled.toLocaleString("en-IN", { maximumFractionDigits: 2 })
                  : "—"}
              </span>
              <InputNumber
                min={0}
                style={{ flex: 1 }}
                placeholder="0"
                value={row.qty as any}
                // A month exploded from daily execution belongs to the register.
                // Editing it here would be rejected on save, so it is shown and
                // locked rather than offered and refused.
                disabled={row.isDerived}
                onChange={(value) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.month === row.month ? { ...r, qty: value === null ? null : Number(value) } : r
                    )
                  )
                }
              />
              <Input
                style={{ flex: 1 }}
                placeholder="remark (optional)"
                value={row.remarks}
                maxLength={500}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r) =>
                      r.month === row.month ? { ...r, remarks: e.target.value } : r
                    )
                  )
                }
              />
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() =>
                  setRows((prev) => prev.filter((r) => r.month !== row.month))
                }
              />
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "#f5f6fa",
          borderRadius: 6,
          marginBottom: 12,
        }}
      >
        <span style={{ color: "#526484" }}>
          Total executed:{" "}
          <b style={{ color: "#022c57" }}>
            {total}
            {uom ? ` ${uom}` : ""}
          </b>
          {workQuantity > 0 ? (
            <span style={{ color: "#8094ae" }}> of {workQuantity}</span>
          ) : null}
        </span>
        {workQuantity > 0 &&
          (overExecuted ? (
            <Tag color="gold">Over by +{overBy}</Tag>
          ) : (
            <Tag color="blue">
              {workQuantity > 0
                ? `${Number(((total / workQuantity) * 100).toFixed(2))}% executed`
                : ""}
            </Tag>
          ))}
      </div>

      {overExecuted && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Actual total exceeds the task quantity"
          description="You can still save — over-execution is recorded as a warning, not an error."
        />
      )}
      {futureRows.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${futureRows.length} month${futureRows.length === 1 ? " is" : "s are"} in the future`}
          description="Check for a wrong month/year — future actuals save with a warning."
        />
      )}

      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="primary" loading={saving} onClick={onSaveClick}>
          Save Actuals
        </Button>
      </div>
    </Modal>
  );
};

export default TaskActualFormModal;
